/**
 * Lossless conversion between the ColorPicker's `(hex, opacity)`
 * pair and a single CSS-color string suitable for storage / for
 * passing to lightweight-charts.
 *
 * Storage rule: opacity == 1 stays as a plain 6-char hex string
 * (#rrggbb) so existing themes round-trip unchanged; opacity < 1
 * upgrades to `rgba(r, g, b, a)`. lightweight-charts accepts both
 * forms wherever it takes a color.
 */

export interface ColorAlpha {
  color: string; // '#rrggbb', lowercase
  opacity: number; // 0..1
}

/** Parse a CSS color string into a (#rrggbb, opacity) pair. Handles
 *  the three forms our codebase writes (6-char hex, 8-char hex with
 *  alpha, rgba()). Anything we can't parse falls back to opaque
 *  black so the chart never silently renders transparent. */
export function parseColor(s: string | undefined): ColorAlpha {
  if (!s) return { color: "#000000", opacity: 1 };
  const six = /^#([0-9a-f]{6})$/i.exec(s);
  if (six) return { color: `#${six[1].toLowerCase()}`, opacity: 1 };
  const eight = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(s);
  if (eight) {
    return {
      color: `#${eight[1].toLowerCase()}`,
      opacity: parseInt(eight[2], 16) / 255,
    };
  }
  const rgba =
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(
      s,
    );
  if (rgba) {
    const r = Number(rgba[1]);
    const g = Number(rgba[2]);
    const b = Number(rgba[3]);
    const hex =
      "#" +
      r.toString(16).padStart(2, "0") +
      g.toString(16).padStart(2, "0") +
      b.toString(16).padStart(2, "0");
    return {
      color: hex,
      opacity: rgba[4] != null ? Math.max(0, Math.min(1, Number(rgba[4]))) : 1,
    };
  }
  return { color: "#000000", opacity: 1 };
}

/** Inverse of `parseColor`. Returns plain #rrggbb when opacity is
 *  effectively 1 to keep saved JSON tidy; otherwise rgba(). */
export function formatColor(color: string, opacity: number): string {
  // 1e-3 tolerance so a 99.9%-from-slider round-trips as opaque.
  if (opacity >= 0.999) {
    return color.toLowerCase();
  }
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (!m) return color;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  // 3 decimals is enough for 256 alpha steps; trim trailing zeros
  // so 1.0 → "1" if the caller passes that through (defensive).
  const a = Math.max(0, Math.min(1, opacity));
  const aStr = a
    .toFixed(3)
    .replace(/\.?0+$/, "");
  return `rgba(${r}, ${g}, ${b}, ${aStr || "0"})`;
}
